/**
 * InviteResidentTenantCard — Iter-191 · Red Team Addendum.
 *
 * Placed inside the PM's Residents (multifamily) / Tenants (12
 * commercial verticals) tab, directly under the Pending Approvals
 * strip. Sends an invite by email, SMS, or both. The Property/Facility
 * ID is pre-pasted and the invitee is pre-authorized so their
 * self-registration skips PM verification.
 */
import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Send, Mail, MessageSquare, UserPlus } from 'lucide-react';
import { roleTermFor } from '../lib/roleTerm';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InviteResidentTenantCard({
  propertyId,
  propertyCode,
  propertyName,
  propertyType,
}) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    unit_number: '',
    send_email: true,
    send_sms: false,
    // Iter-193 · Trashcan intake — PMs decide upfront whether they'll
    // supply the resident's trashcan themselves or ask NECS to deliver
    // one. When 'necs_provide' is picked we fire a same-day fulfillment
    // notification to Customer Services so Ops can queue delivery.
    trashcan_source: 'pm_provide', // pm_provide | necs_provide | not_needed
  });
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const invitee = roleTermFor(propertyType);

  if (!propertyId) return null;

  async function submit(e) {
    e.preventDefault();
    if (!form.email && !form.phone) {
      toast.error('Enter an email, a phone number, or both.');
      return;
    }
    if (!form.send_email && !form.send_sms) {
      toast.error('Pick at least one channel (email or SMS).');
      return;
    }
    // Iter-193 · Unit number is required (not optional).
    if (!form.unit_number.trim()) {
      toast.error('Unit number is required for every invite.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/properties/${propertyId}/invite-resident`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Invite failed.');
      setLast(data);
      const channels = [
        data.email_sent && 'email',
        data.sms_sent && 'SMS',
      ].filter(Boolean).join(' + ') || 'log only';
      toast.success(`Invite sent to ${form.email || form.phone} (${channels}).`);
      if (data.trashcan_request === 'necs_provide') {
        toast.info('NECS Customer Service has been notified to deliver a trashcan today.');
      }
      setForm({ ...form, first_name: '', last_name: '', email: '', phone: '', unit_number: '', trashcan_source: 'pm_provide' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      className="border-2 border-[#FFD60A]/60 bg-gradient-to-br from-[#FFFBEA] to-white p-5 space-y-4"
      data-testid="invite-resident-tenant-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-[#FFD60A] flex items-center justify-center flex-shrink-0">
            <UserPlus className="h-5 w-5 text-black" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#7a6300]">
              Invite New {invitee}s
            </div>
            <div className="text-sm font-bold text-zinc-900">
              {invitee} A/H · Account Holder
            </div>
          </div>
        </div>
        {propertyCode && (
          <div className="rounded-md bg-black text-[#FFD60A] px-3 py-1.5 font-mono text-xs tracking-wide">
            {propertyCode}
          </div>
        )}
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-bold text-zinc-800">First name</Label>
          <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-white border-zinc-400 text-zinc-900 placeholder-zinc-400 focus-visible:ring-[#FFD60A]" data-testid="invite-first-name" />
        </div>
        <div>
          <Label className="text-xs font-bold text-zinc-800">Last name</Label>
          <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-white border-zinc-400 text-zinc-900 placeholder-zinc-400 focus-visible:ring-[#FFD60A]" data-testid="invite-last-name" />
        </div>
        <div>
          <Label className="text-xs font-bold text-zinc-800">Email address</Label>
          <Input type="email" placeholder="resident@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-zinc-400 text-zinc-900 placeholder-zinc-400 focus-visible:ring-[#FFD60A]" data-testid="invite-email" />
        </div>
        <div>
          <Label className="text-xs font-bold text-zinc-800">Phone (E.164)</Label>
          <Input type="tel" placeholder="+18175551234" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white border-zinc-400 text-zinc-900 placeholder-zinc-400 focus-visible:ring-[#FFD60A]" data-testid="invite-phone" />
        </div>
        <div>
          <Label className="text-xs font-bold text-zinc-800">
            Unit number <span className="text-red-600">*</span>
          </Label>
          <Input
            required
            value={form.unit_number}
            onChange={(e) => setForm({ ...form, unit_number: e.target.value })}
            placeholder="e.g., 101"
            className="bg-white border-zinc-400 text-zinc-900 placeholder-zinc-400 focus-visible:ring-[#FFD60A]"
            data-testid="invite-unit"
          />
        </div>
        <div>
          <Label className="text-xs font-bold text-zinc-800">Property / Facility ID</Label>
          <Input value={propertyCode || ''} readOnly disabled className="font-mono bg-zinc-100 text-zinc-900 border-zinc-400" data-testid="invite-necs-code" />
        </div>

        {/* Iter-193 · Trashcan intake. NECS supplies a new trashcan
            for every new resident during contract, but sometimes the
            PM has one in stock. PMs pick the source here. When 'NECS
            provides' is selected, Customer Service is notified so Ops
            can fulfill same-day. */}
        <div className="md:col-span-2 rounded-lg border-2 border-[#C9A962]/60 bg-white p-3 space-y-2" data-testid="invite-trashcan-block">
          <div className="text-xs font-bold uppercase tracking-widest text-[#7a6300]">
            Trashcan for this {invitee.toLowerCase()}
          </div>
          <p className="text-[11px] text-zinc-700">
            NECS provides a new trashcan for every new {invitee.toLowerCase()} during contract.
            If the property has one in stock, pick "Property Management provides".
            Otherwise request NECS and Customer Service will delegate Operations to deliver <b>today</b>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-1">
            {[
              { key: 'pm_provide',  label: 'Property Management provides', sub: 'PM has one in stock' },
              { key: 'necs_provide', label: 'Request NECS to provide',      sub: 'Ops delivers today' },
              { key: 'not_needed',  label: `${invitee} already has one`,      sub: 'Skip fulfillment' },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setForm({ ...form, trashcan_source: opt.key })}
                className={`text-left p-3 rounded-md border-2 font-bold text-xs transition ${
                  form.trashcan_source === opt.key
                    ? 'border-[#FFD60A] bg-[#FFFBEA]'
                    : 'border-zinc-300 bg-white hover:bg-zinc-50'
                }`}
                data-testid={`invite-trashcan-${opt.key}`}
              >
                <div className="text-zinc-900">{opt.label}</div>
                <div className="text-[10px] font-normal text-zinc-500 mt-0.5">{opt.sub}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-2 flex flex-wrap items-center gap-4 pt-1">
          <label className="inline-flex items-center gap-2 text-sm font-bold text-zinc-900 cursor-pointer">
            <input
              type="checkbox"
              checked={form.send_email}
              onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
              className="h-4 w-4 accent-[#FFD60A]"
              data-testid="invite-channel-email"
            />
            <Mail className="h-3.5 w-3.5 text-zinc-800" />
            <span className="text-zinc-900">Send by email</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm font-bold text-zinc-900 cursor-pointer">
            <input
              type="checkbox"
              checked={form.send_sms}
              onChange={(e) => setForm({ ...form, send_sms: e.target.checked })}
              className="h-4 w-4 accent-[#FFD60A]"
              data-testid="invite-channel-sms"
            />
            <MessageSquare className="h-3.5 w-3.5 text-zinc-800" />
            <span className="text-zinc-900">Send by SMS</span>
          </label>
          <div className="ml-auto">
            <Button
              type="submit"
              disabled={busy}
              className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
              data-testid="invite-submit"
            >
              <Send className="h-4 w-4 mr-1" /> {busy ? 'Sending…' : `Invite ${invitee} A/H`}
            </Button>
          </div>
        </div>
      </form>

      {last && (
        <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="invite-last-result">
          Invite sent · <b>{last.invitee_label}</b> ·
          {' '}Email: {last.email_sent === null ? '—' : last.email_sent ? '✓' : '✗'}
          {' · '}SMS: {last.sms_sent === null ? '—' : last.sms_sent ? '✓' : '✗'}
        </div>
      )}

      <p className="text-[11px] text-zinc-500 leading-snug">
        Recipients arrive on the registration form with your Property/Facility ID pre-filled. Because
        you invited them, they skip the PM verification step and land directly with NECS Customer
        Service for activation.
      </p>
    </Card>
  );
}
